import Alert from '@pelckmans/elementis/components/alert';
import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';
import SearchField from '@pelckmans/elementis/components/search-field';
import { isAxiosError } from 'axios';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isScorableMedialink, type Medialink } from '../../api/medialink';
import type { SlideSet } from '../../api/slide-set';
import type { UserMaterial } from '../../api/user-material';
import ModalContext from '../../contexts/ModalContext';
import useThrowAsyncError from '../../hooks/useThrowAsyncError';
import { buildGroupProps } from '../../utils/elementisUtils';
import { TOAST_ID } from '../../constants/ToastIds';
import {
  deleteSharedMaterials,
  getInitialSingleSharedMaterial,
  isSchoolAudience,
  postSharedMaterials,
  putSingleSharedMaterial,
  type Audience,
  type UserSharedMaterial,
} from './sharedMaterialApi';
import { ShareModalBody } from './ShareModalBody';
import ShareSelectionModal from './ShareSelectionModal';
import useMemberSearch from './useMemberSearch';
import { ShareModalContextProvider, useShareModalContext } from './useShareModalContext';
import { useToast } from '@pelckmans/elementis/components/toast';
import { useDefaultToast } from '../../hooks/useDefaultToast';

import './share-modal.css';

const BASE = 'PAGES.TOC_LEVEL.MODALS.SHARE';

export type ContentVariant = 'no-groups' | 'pelckmans-slide-set' | 'no-filter-result' | 'has-content';

type SubmitError = 'no-selection' | 'invalid-dates';

const haveAudiencesBeenDeselected = (
  initialAudiences: UserSharedMaterial['audiences'],
  currentAudiences: Audience[],
) => {
  if (!initialAudiences || initialAudiences.length === 0) return false;

  if (initialAudiences.length > currentAudiences.length) return true;

  // Saving replaces a school share with group shares
  if (initialAudiences.some(isSchoolAudience)) return true;

  for (const initialAudience of initialAudiences as Audience[]) {
    const currentAudience =
      initialAudience.audienceType === 'group'
        ? currentAudiences.find(a => a.groupId === initialAudience.groupId && a.audienceType === 'group')
        : currentAudiences.find(
            a => 'userId' in a && a.userId === initialAudience.userId && a.groupId === initialAudience.groupId,
          );
    if (!currentAudience) return true;
    // Check if assignment data has been removed
    if (initialAudience.assignment && !currentAudience.assignment) return true;
  }

  return false;
};

interface ShareModalProps {
  id: string;
  moduleId: string;
  nodeId: string;
  userMaterials?: UserMaterial[];
  slideSets?: SlideSet[];
  medialinks?: Medialink[];
  onSubmitted?: () => void;
}

function ShareModal({ id, moduleId, nodeId, userMaterials, slideSets, medialinks, onSubmitted }: ShareModalProps) {
  const { t } = useTranslation();
  const handleError = useThrowAsyncError();
  const { searchValue, setSearchValue, handleSearch, handleClearSearch, searchResults, isSearching } =
    useMemberSearch();
  const { addToast } = useToast();
  const { failedToast, successToast } = useDefaultToast();

  const {
    groupsBySchool,
    isLoading,
    selectedGroups,
    selectedMembers,
    handleUpdateAudience,
    resetAndMapInitialAudiences,
  } = useShareModalContext();
  const { showModal, closeModal } = useContext(ModalContext);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitErrors, setSubmitErrors] = useState<SubmitError[]>([]);
  const singleUserSharedMaterialRef = useRef<UserSharedMaterial | undefined>(undefined);

  const containsExistingShares = useMemo(
    () =>
      userMaterials?.some(userMaterial => userMaterial.shared || userMaterial.assignment) ||
      slideSets?.some(slideSet => slideSet.shared) ||
      medialinks?.some(medialink => medialink.shares?.length > 0 || medialink.assignments?.length > 0) ||
      false,
    [userMaterials, slideSets, medialinks],
  );

  const containsNonScorable = useMemo(
    () =>
      userMaterials?.some(userMaterial => !userMaterial.userMaxScore) ||
      (slideSets?.length || 0) > 0 ||
      medialinks?.some(medialink => !isScorableMedialink(medialink)) ||
      false,
    [userMaterials, slideSets, medialinks],
  );

  const containsNonShareableAsAssignment = (slideSets?.length || 0) > 0 || false;

  const containsNonShareable = slideSets?.some(set => !set.userId) || false;

  const audiencesDeselected = useMemo(
    () =>
      haveAudiencesBeenDeselected(singleUserSharedMaterialRef.current?.audiences || [], [
        ...selectedGroups,
        ...selectedMembers,
      ]),
    [singleUserSharedMaterialRef, selectedGroups, selectedMembers],
  );
  const totalSelected = (userMaterials?.length || 0) + (slideSets?.length || 0) + (medialinks?.length || 0);

  useEffect(() => {
    const fetchInitialSharedMaterial = async () => {
      try {
        const materialId = userMaterials?.[0]?.id || slideSets?.[0]?.id || medialinks?.[0]?.id;
        if (!materialId) return;

        const singleShare = await getInitialSingleSharedMaterial(moduleId, nodeId, materialId);

        resetAndMapInitialAudiences(singleShare.audiences);

        if (singleShare.audiences.length > 0) singleUserSharedMaterialRef.current = singleShare;
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) return;

        handleError(err);
      }
    };

    // School audiences map onto groups, so wait until those are loaded
    if (!isLoading && totalSelected === 1 && containsExistingShares) fetchInitialSharedMaterial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containsExistingShares, isLoading]);

  if (totalSelected === 0) return null;

  let contentVariant: ContentVariant;
  if (Object.keys(groupsBySchool).length === 0) {
    contentVariant = 'no-groups';
  } else if (isSearching && searchResults.length === 0) {
    contentVariant = 'no-filter-result';
  } else if (containsNonShareable) {
    contentVariant = 'pelckmans-slide-set';
  } else {
    contentVariant = 'has-content';
  }

  const showShareSelectionModal = () =>
    showModal(
      <ShareSelectionModal
        id="share-selection-modal"
        userMaterials={userMaterials}
        slideSets={slideSets}
        medialinks={medialinks}
      />,
    );

  const handleClear = () => resetAndMapInitialAudiences([]);

  const handleClose = () => closeModal(id, true);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const audiences = [...selectedGroups, ...selectedMembers];

    const errors: SubmitError[] = [];
    audiences.forEach(audience => {
      if (audience.assignment && !(audience.assignment.from || audience.assignment.to)) {
        const memberId = 'userId' in audience ? audience.userId : undefined;
        handleUpdateAudience({ groupId: audience.groupId, memberId, dateError: true });
        if (!errors.includes('invalid-dates')) {
          errors.push('invalid-dates');
        }
      }
    });

    if (!containsExistingShares && audiences.length === 0) errors.push('no-selection');

    if (errors.length > 0) {
      setSubmitErrors(errors);
      setIsSubmitting(false);
      return;
    }

    const materials = {
      ...(userMaterials && userMaterials.length > 0 && { userMaterials: userMaterials.map(material => material.id) }),
      ...(slideSets && slideSets.length > 0 && { slideSets: slideSets.map(set => set.id) }),
      ...(medialinks && medialinks.length > 0 && { mediaLinks: medialinks.map(link => link.id) }),
    };

    // Keep school shares without a matching group, the modal can't show or edit them
    const groupSchoolIds = Object.values(groupsBySchool)
      .flat()
      .map(group => group.school.id);
    const hiddenSchoolAudiences = (singleUserSharedMaterialRef.current?.audiences ?? [])
      .filter(isSchoolAudience)
      .filter(audience => !groupSchoolIds.includes(audience.schoolId));

    try {
      if (audiences.length === 0 && hiddenSchoolAudiences.length === 0) {
        successToast();
        await deleteSharedMaterials(moduleId, nodeId, materials);
      } else {
        if (singleUserSharedMaterialRef.current) {
          await putSingleSharedMaterial({
            ...singleUserSharedMaterialRef.current,
            audiences: [...audiences, ...hiddenSchoolAudiences],
          });
        } else {
          await postSharedMaterials(moduleId, nodeId, audiences, materials);
        }
        addToast({
          id: TOAST_ID.MATERIAL_SHARE_SUCCESS,
          title: t(`${BASE}.TOAST.TITLE`),
          children: t(`${BASE}.TOAST.MESSAGE`),
          loadingAriaLabel: t('TOAST.LOADING_LABEL'),
          variant: 'success',
        });
      }

      handleClose();
      onSubmitted?.();
    } catch (err) {
      failedToast();
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog id={id} modal size="md" onClose={handleClose} aria-label={t(`${BASE}.A11Y_LABEL`)}>
      <Dialog.Header
        title={t(`${BASE}.HEADER.TITLE`)}
        onClose={handleClose}
        aria-label={t(`${BASE}.HEADER.A11Y_LABEL`)}
      >
        <SearchField
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
          onClear={handleClearSearch}
          onSearch={handleSearch}
          aria-label={t(`${BASE}.SEARCH_FIELD.A11Y_LABEL`)}
          placeholder={t(`${BASE}.SEARCH_FIELD.PLACEHOLDER`)}
          clearActionLabel={t(`${BASE}.SEARCH_FIELD.CLEAR_LABEL`)}
          searchActionLabel={t(`${BASE}.SEARCH_FIELD.SEARCH_LABEL`)}
        />
      </Dialog.Header>
      <Dialog.Body
        alertGroupProps={
          contentVariant === 'has-content'
            ? buildGroupProps(
                <>
                  <Alert
                    prominence="info"
                    actions={buildGroupProps(
                      <Button prominence="info" size="sm" onClick={showShareSelectionModal}>
                        {t(`${BASE}.SELECTION.ACTION`)}
                      </Button>,
                    )}
                  >
                    {totalSelected === 1
                      ? t(`${BASE}.SELECTION.SINGLE`, {
                          name: userMaterials?.[0]?.name || slideSets?.[0]?.name || medialinks?.[0]?.name,
                        })
                      : t(`${BASE}.SELECTION.MULTIPLE`, { count: totalSelected })}{' '}
                    {containsNonShareableAsAssignment && t(`${BASE}.SELECTION.NO_ASSIGNMENT`)}
                    {!containsNonShareableAsAssignment && containsNonScorable && t(`${BASE}.SELECTION.REPEATABLE`)}
                  </Alert>
                  {(audiencesDeselected || (containsExistingShares && totalSelected > 1)) && (
                    <Alert prominence="warning">{t(`${BASE}.WARNINGS.SHARE_OVERRIDE`)}</Alert>
                  )}
                  {submitErrors.includes('no-selection') && (
                    <Alert prominence="danger">{t(`${BASE}.ERRORS.NO_SELECTION`)}</Alert>
                  )}
                  {submitErrors.includes('invalid-dates') && (
                    <Alert prominence="danger">{t(`${BASE}.ERRORS.INVALID_DATES`)}</Alert>
                  )}
                </>,
              )
            : undefined
        }
      >
        <ShareModalBody
          isSearching={isSearching}
          searchResults={searchResults}
          containsNonScorable={containsNonScorable}
          containsNonShareableAsAssignment={containsNonShareableAsAssignment}
          contentVariant={contentVariant}
          onClearSearch={handleClearSearch}
        />
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsLeft={buildGroupProps(
          contentVariant === 'has-content' ? (
            <Button
              prominence="ghost"
              onClick={handleClear}
              key="clear"
              disabled={selectedGroups.length === 0 && selectedMembers.length === 0}
            >
              {t('ACTIONS.CLEAR_SELECTION')}
            </Button>
          ) : (
            []
          ),
        )}
        buttonGroupPropsRight={buildGroupProps(
          <>
            <Button prominence="neutral" onClick={handleClose} key="cancel">
              {t('ACTIONS.CANCEL')}
            </Button>
            {contentVariant === 'has-content' ? (
              <Button prominence="success" onClick={handleSubmit} busy={isSubmitting} key="submit">
                {containsExistingShares ? t(`${BASE}.ACTIONS.SUBMIT_EDIT`) : t(`${BASE}.ACTIONS.SUBMIT`)}
              </Button>
            ) : null}
          </>,
        )}
      ></Dialog.Footer>
    </Dialog>
  );
}

const ShareModalWrapper = (props: ShareModalProps) => (
  <ShareModalContextProvider>
    <ShareModal {...props} />
  </ShareModalContextProvider>
);

export default ShareModalWrapper;
